import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft, CheckCircle2, AlertTriangle, Upload, Loader2, ImageIcon,
  Send, Clock, DollarSign, User, Package, MessageSquare, Star, Heart,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { GlassCard, API } from '../vendor/GlassCard';
import RateAndTipModal from './RateAndTipModal';
import FileGallery from './FileGallery';

/** Tell other dashboard surfaces (Overview, EarningsPulse) to refetch instantly. */
function broadcastVendorStatsRefresh() {
  try { window.dispatchEvent(new CustomEvent('vendor-stats-refresh')); } catch {}
}

const STATUS_STYLES = {
  pending_payment:     { label: 'Awaiting Payment',  bg: 'bg-amber-500/15 text-amber-200 border-amber-400/30' },
  active:              { label: 'In Progress',       bg: 'bg-blue-500/15 text-blue-200 border-blue-400/30' },
  delivered:           { label: 'Delivered',         bg: 'bg-emerald-500/15 text-emerald-200 border-emerald-400/30' },
  revision_requested:  { label: 'Revision Requested', bg: 'bg-orange-500/15 text-orange-200 border-orange-400/30' },
  completed:           { label: 'Completed',         bg: 'bg-fuchsia-500/15 text-fuchsia-200 border-fuchsia-400/30' },
  cancelled:           { label: 'Cancelled',         bg: 'bg-red-500/15 text-red-200 border-red-400/30' },
};

export default function OrderDetail() {
  const { orderId } = useParams();
  const { getAuthHeaders } = useAuth();
  const [order, setOrder] = useState(null);
  const [other, setOther] = useState({});
  const [role, setRole] = useState('vendor');
  const [autoDays, setAutoDays] = useState(3);
  const [loading, setLoading] = useState(true);

  // Delivery composer state (vendor only)
  const [delivMessage, setDelivMessage] = useState('');
  const [delivFiles, setDelivFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [delivering, setDelivering] = useState(false);
  const fileInputRef = useRef(null);

  // Revision composer (buyer only)
  const [revisionNote, setRevisionNote] = useState('');
  const [actioning, setActioning] = useState(false);

  // Rate-and-tip modal (buyer only, opens after approve OR via CTA on completed/unrated orders)
  const [rateModalOpen, setRateModalOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/custom-orders/${orderId}`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setOrder(data.order);
        setOther(data.other_party || {});
        setRole(data.viewer_role || 'vendor');
        setAutoDays(data.auto_complete_days || 3);
      } else {
        const d = await res.json().catch(() => ({}));
        toast.error(d.detail || 'Order not found');
      }
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  useEffect(() => { load(); }, [load]);

  // Vendor: upload delivery files
  const handleFilesPicked = async (filesRaw) => {
    const files = Array.from(filesRaw || []);
    if (!files.length) return;
    setUploading(true);
    const uploaded = [];
    for (const f of files) {
      const fd = new FormData();
      fd.append('file', f);
      try {
        const r = await fetch(`${API}/media/upload?category=delivery`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: fd,
        });
        const d = await r.json();
        if (r.ok && (d.url || d.media_url)) uploaded.push(d.url || d.media_url);
        else toast.error(d.detail || `Failed: ${f.name}`);
      } catch { toast.error(`Failed: ${f.name}`); }
    }
    setDelivFiles((p) => [...p, ...uploaded]);
    setUploading(false);
  };

  const submitDelivery = async () => {
    if (!delivMessage.trim()) { toast.error('Add a delivery message'); return; }
    setDelivering(true);
    try {
      const r = await fetch(`${API}/custom-orders/${orderId}/deliver`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: delivMessage, file_urls: delivFiles }),
      });
      if (r.ok) {
        toast.success('Delivery submitted! Buyer has been notified.');
        setDelivMessage(''); setDelivFiles([]);
        load();
        broadcastVendorStatsRefresh();
      } else {
        const d = await r.json().catch(() => ({}));
        toast.error(d.detail || 'Delivery failed');
      }
    } finally { setDelivering(false); }
  };

  const approve = async () => {
    setActioning(true);
    try {
      const r = await fetch(`${API}/custom-orders/${orderId}/approve`, { method: 'POST', headers: getAuthHeaders() });
      if (r.ok) {
        toast.success('Order approved & completed!');
        await load();
        broadcastVendorStatsRefresh();
        // Immediately prompt the buyer to leave a rating + optional tip.
        setRateModalOpen(true);
      } else { const d = await r.json().catch(() => ({})); toast.error(d.detail || 'Approve failed'); }
    } finally { setActioning(false); }
  };

  // After returning from Stripe Checkout for a tip, show a thank-you toast.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('tipped') === '1') {
      toast.success('Thanks for the tip — 100% went to the vendor!');
      // Clean the URL so a refresh doesn't re-fire the toast.
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('tip_cancelled') === '1') {
      toast.info('Tip cancelled — no charge.');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const requestRevision = async () => {
    if (!revisionNote.trim()) { toast.error('Add a note explaining what to revise'); return; }
    setActioning(true);
    try {
      const r = await fetch(`${API}/custom-orders/${orderId}/request-revision`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: revisionNote }),
      });
      if (r.ok) { toast.success('Revision requested — vendor will be notified.'); setRevisionNote(''); load(); broadcastVendorStatsRefresh(); }
      else { const d = await r.json().catch(() => ({})); toast.error(d.detail || 'Failed'); }
    } finally { setActioning(false); }
  };

  const startCheckout = async () => {
    setActioning(true);
    try {
      const r = await fetch(`${API}/custom-orders/${orderId}/create-checkout-session`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ origin_url: window.location.origin }),
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok && d.checkout_url) {
        // Hand off to Stripe Checkout — wallets (Apple Pay / Google Pay /
        // Link) appear automatically when the device supports them.
        window.location.href = d.checkout_url;
      } else {
        toast.error(d.detail || 'Could not start checkout');
        setActioning(false);
      }
    } catch (err) {
      toast.error('Network error');
      setActioning(false);
    }
  };

  // Auto-confirm when buyer returns from Stripe Checkout with ?paid=1
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('paid') === '1') {
      // Stripe webhook should already have activated the order — but to be
      // safe against webhook delays / failures, we proactively ask the
      // backend to re-check the Checkout Session and self-heal the order.
      // No more reliance on the manual mark-paid stub.
      const t = setTimeout(async () => {
        try {
          await fetch(`${API}/custom-orders/${orderId}/sync-payment`, {
            method: 'POST', headers: getAuthHeaders(),
          });
        } catch { /* ignore — load() below will surface the real state */ }
        load();
        // Clean the URL so a refresh doesn't re-fire.
        window.history.replaceState({}, '', window.location.pathname);
      }, 1200);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const markPaid = startCheckout; // alias: Pay button now goes to Stripe

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-fuchsia-300" /></div>;
  if (!order) return (
    <div className="text-center py-16 text-zinc-400">Order not found. <Link to={role === 'buyer' ? '/my-orders' : '/dashboard/vendor/orders'} className="text-fuchsia-300 hover:underline">Back</Link></div>
  );

  const status = STATUS_STYLES[order.status] || STATUS_STYLES.active;
  const orig = order.original_form || {};
  const backLink = role === 'buyer' ? '/my-orders' : '/dashboard/vendor/orders';
  const isVendor = role === 'vendor';
  const isBuyer = role === 'buyer';

  return (
    <div className="space-y-5 max-w-4xl mx-auto" data-testid="order-detail">
      <Link to={backLink} className="text-fuchsia-300 text-sm inline-flex items-center gap-1 hover:underline">
        <ArrowLeft size={14} /> Back to orders
      </Link>

      {/* Header */}
      <GlassCard className="p-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0 flex-1">
            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold border ${status.bg}`} data-testid="order-status-pill">
              {status.label.toUpperCase()}
            </span>
            <h1 className="text-2xl text-white font-syne font-bold mt-2">{order.title}</h1>
            <p className="text-zinc-400 text-xs mt-1">Order #{order.order_id.slice(-8)} · created {new Date(order.created_at).toLocaleDateString()}</p>
          </div>
          <div className="text-right">
            <p className="text-zinc-400 text-[10px] uppercase tracking-wider">Price</p>
            <p className="text-2xl text-white font-bold">${order.price.toFixed(2)}</p>
            <p className="text-zinc-400 text-xs">{order.delivery_days}d delivery</p>
            {isVendor && (
              <p className="text-[10px] text-emerald-300/80 mt-1">
                You earn ${(order.price * 0.90).toFixed(2)}
                <span className="text-zinc-500"> (50% deposit on payment, 40% on approval)</span>
              </p>
            )}
          </div>
        </div>
        <div className="mt-4 flex items-center gap-3 pt-4 border-t border-white/10">
          <div className="w-10 h-10 rounded-full bg-fuchsia-500/15 flex items-center justify-center overflow-hidden">
            {other.picture ? <img src={other.picture} alt="" className="w-full h-full object-cover" /> : <User size={16} className="text-fuchsia-300" />}
          </div>
          <div>
            <p className="text-zinc-400 text-[10px] uppercase tracking-wider">{isVendor ? 'Buyer' : 'Vendor'}</p>
            <p className="text-white font-bold text-sm">{other.name || other.email || '—'}</p>
          </div>
        </div>
      </GlassCard>

      {/* Pending payment box */}
      {order.status === 'pending_payment' && (
        <GlassCard className="p-5 border-amber-400/30 bg-amber-500/5">
          <div className="flex items-start gap-3">
            <Clock className="text-amber-300 flex-shrink-0 mt-0.5" size={18} />
            <div className="flex-1">
              <p className="text-amber-100 font-bold">{isBuyer ? 'Payment required' : 'Waiting on buyer payment'}</p>
              <p className="text-amber-100/70 text-xs mt-1">
                {isBuyer
                  ? `Pay $${order.price.toFixed(2)} to start the ${order.delivery_days}-day delivery clock.`
                  : `Buyer hasn't paid yet. We'll notify you the moment they do.`}
              </p>
              {isBuyer && (
                <>
                  <button
                    onClick={startCheckout}
                    disabled={actioning}
                    className="mt-3 inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-gradient-to-r from-amber-400 to-orange-500 text-[#0E0E23] text-sm font-bold disabled:opacity-50 shadow-[0_8px_24px_-8px_rgba(251,191,36,0.6)]"
                    data-testid="pay-now-btn"
                  >
                    {actioning && <Loader2 size={14} className="animate-spin" />}
                    <DollarSign size={14} /> Pay ${order.price.toFixed(2)} →
                  </button>
                  <p className="text-[10px] text-amber-100/70 mt-2 flex items-center gap-2 flex-wrap">
                    <span>Secure checkout via Stripe.</span>
                    <span className="inline-flex items-center gap-1">
                      <span className="px-1.5 py-0.5 rounded bg-black/30 text-white text-[9px] font-bold tracking-wider">🍎 Pay</span>
                      <span className="px-1.5 py-0.5 rounded bg-black/30 text-white text-[9px] font-bold tracking-wider">G Pay</span>
                      <span className="px-1.5 py-0.5 rounded bg-black/30 text-white text-[9px] font-bold tracking-wider">CARD</span>
                      <span className="px-1.5 py-0.5 rounded bg-black/30 text-white text-[9px] font-bold tracking-wider">LINK</span>
                    </span>
                  </p>
                </>
              )}
            </div>
          </div>
        </GlassCard>
      )}

      {/* Original request snapshot — always visible */}
      <GlassCard className="p-6" data-testid="order-original-form">
        <p className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold mb-3 flex items-center gap-1.5">
          <Package size={11} /> Original Request
        </p>
        <p className="text-zinc-200 text-sm whitespace-pre-wrap leading-relaxed bg-white/[0.03] border border-white/10 rounded-xl p-4">
          {orig.description || order.description || '—'}
        </p>
        {(orig.attachment_urls || []).length > 0 && (
          <div className="mt-4">
            <p className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold mb-2 flex items-center gap-1.5">
              <ImageIcon size={11} /> Reference files ({orig.attachment_urls.length})
            </p>
            <FileGallery files={orig.attachment_urls} />
          </div>
        )}
        {order.scope_note && (
          <div className="mt-4 p-3 rounded-xl bg-fuchsia-500/10 border border-fuchsia-400/20">
            <p className="text-[10px] uppercase tracking-wider text-fuchsia-300 font-bold mb-1">Vendor's scope note</p>
            <p className="text-zinc-200 text-sm">{order.scope_note}</p>
          </div>
        )}
      </GlassCard>

      {/* Deliveries */}
      {order.deliveries?.length > 0 && (
        <GlassCard className="p-6" data-testid="order-deliveries">
          <p className="text-[10px] uppercase tracking-wider text-emerald-300 font-bold mb-3 flex items-center gap-1.5">
            <CheckCircle2 size={11} /> Vendor's Delivery ({order.deliveries.length})
          </p>
          {order.deliveries.map((d, i) => (
            <div key={d.delivery_id} className="rounded-xl bg-emerald-500/5 border border-emerald-400/20 p-4 mb-3 last:mb-0">
              <p className="text-emerald-100 text-xs mb-2">Delivery #{i + 1} · {new Date(d.delivered_at).toLocaleString()}</p>
              <p className="text-zinc-200 text-sm whitespace-pre-wrap">{d.message}</p>
              {(d.file_urls || []).length > 0 && (
                <div className="mt-3">
                  <FileGallery files={d.file_urls} />
                </div>
              )}
            </div>
          ))}
        </GlassCard>
      )}

      {/* Delivered → buyer can approve or revise */}
      {order.status === 'delivered' && isBuyer && (
        <GlassCard className="p-5 border-emerald-400/30 bg-emerald-500/5">
          <p className="text-emerald-100 font-bold">Your delivery is here!</p>
          <p className="text-emerald-100/70 text-xs mt-1">
            Approve to mark complete + release payment, or request a revision within {autoDays} days
            ({order.auto_complete_at && `auto-completes ${new Date(order.auto_complete_at).toLocaleDateString()}`}).
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <button onClick={approve} disabled={actioning} className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold text-sm disabled:opacity-50 inline-flex items-center gap-2" data-testid="approve-order-btn">
              {actioning && <Loader2 size={14} className="animate-spin" />}
              <CheckCircle2 size={14} /> Approve & Complete
            </button>
          </div>
          <div className="mt-4">
            <p className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold mb-1.5">Or request a revision:</p>
            <textarea
              value={revisionNote}
              onChange={(e) => setRevisionNote(e.target.value)}
              rows={3}
              placeholder="What needs to change?"
              className="w-full bg-white/5 border border-white/10 rounded-xl text-white text-sm p-3"
              data-testid="revision-note"
            />
            <button onClick={requestRevision} disabled={actioning} className="mt-2 px-4 py-2 rounded-xl bg-orange-500/20 hover:bg-orange-500/30 text-orange-100 text-sm font-bold inline-flex items-center gap-2" data-testid="request-revision-btn">
              <AlertTriangle size={13} /> Request Revision
            </button>
          </div>
        </GlassCard>
      )}

      {/* Vendor: deliver / re-deliver after revision */}
      {(order.status === 'active' || order.status === 'revision_requested') && isVendor && (
        <GlassCard className="p-5 border-fuchsia-400/30 bg-fuchsia-500/5" data-testid="vendor-delivery-composer">
          <p className="text-white font-bold flex items-center gap-2">
            <Upload size={16} className="text-fuchsia-300" />
            {order.status === 'revision_requested' ? 'Submit Revision' : 'Submit Final Delivery'}
          </p>
          <p className="text-zinc-400 text-xs mt-1">
            Buyer has {autoDays} days to approve after you deliver, otherwise it auto-completes.
          </p>
          <textarea
            value={delivMessage}
            onChange={(e) => setDelivMessage(e.target.value)}
            rows={4}
            placeholder="Describe what's included. Anything the buyer needs to know about your delivery."
            className="mt-3 w-full bg-white/5 border border-white/10 rounded-xl text-white text-sm p-3"
            data-testid="delivery-message"
          />
          <div className="mt-3">
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 border border-white/10 hover:border-fuchsia-400/40 text-zinc-200 text-sm disabled:opacity-50"
              data-testid="delivery-upload-btn"
            >
              {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
              Attach files (images, PDFs, videos)
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,application/pdf,video/*"
              className="hidden"
              onChange={(e) => handleFilesPicked(e.target.files)}
            />
            {delivFiles.length > 0 && (
              <div className="mt-3">
                <FileGallery files={delivFiles} label={`${delivFiles.length} file${delivFiles.length === 1 ? '' : 's'} ready to send`} />
              </div>
            )}
          </div>
          <button
            onClick={submitDelivery}
            disabled={delivering || uploading}
            className="mt-4 px-5 py-2.5 rounded-xl bg-gradient-to-r from-fuchsia-500 to-violet-600 text-white font-bold text-sm disabled:opacity-50 inline-flex items-center gap-2"
            data-testid="submit-delivery-btn"
          >
            {delivering && <Loader2 size={14} className="animate-spin" />}
            <Send size={14} /> Submit Delivery
          </button>
        </GlassCard>
      )}

      {/* Revision history */}
      {order.revisions?.length > 0 && (
        <GlassCard className="p-5" data-testid="order-revisions">
          <p className="text-[10px] uppercase tracking-wider text-orange-300 font-bold mb-2.5 flex items-center gap-1.5">
            <AlertTriangle size={11} /> Revision Requests ({order.revisions.length})
          </p>
          {order.revisions.map((r) => (
            <div key={r.revision_id} className="rounded-xl bg-orange-500/5 border border-orange-400/20 p-3 mb-2 last:mb-0">
              <p className="text-orange-100/60 text-[10px]">{new Date(r.requested_at).toLocaleString()}</p>
              <p className="text-zinc-200 text-sm mt-1 whitespace-pre-wrap">{r.note}</p>
            </div>
          ))}
        </GlassCard>
      )}

      {/* Timeline */}
      {/* Completed → rating block (visible to both sides) */}
      {order.status === 'completed' && (
        <GlassCard className="p-5" data-testid="rating-block">
          {order.rating ? (
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold">Buyer Rating</p>
                <div className="flex items-center gap-1.5">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star
                      key={n}
                      size={18}
                      className={n <= Math.round(order.rating.average) ? 'fill-yellow-400 text-yellow-400' : 'text-zinc-700'}
                      strokeWidth={1.5}
                    />
                  ))}
                  <span className="ml-2 text-white font-bold text-base" data-testid="rating-average">
                    {order.rating.average?.toFixed(1)}
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3 text-xs">
                <div className="rounded-lg bg-white/[0.03] border border-white/10 p-2.5">
                  <p className="text-zinc-400">As described</p>
                  <p className="text-white font-bold mt-0.5">{order.rating.delivered_as_described}/5</p>
                </div>
                <div className="rounded-lg bg-white/[0.03] border border-white/10 p-2.5">
                  <p className="text-zinc-400">Communication</p>
                  <p className="text-white font-bold mt-0.5">{order.rating.communication}/5</p>
                </div>
                <div className="rounded-lg bg-white/[0.03] border border-white/10 p-2.5">
                  <p className="text-zinc-400">Buy again</p>
                  <p className="text-white font-bold mt-0.5">{order.rating.buy_again}/5</p>
                </div>
              </div>
              {order.rating.comment && (
                <p className="text-zinc-300 text-sm italic mt-3 border-l-2 border-fuchsia-400/40 pl-3" data-testid="rating-comment-display">
                  &ldquo;{order.rating.comment}&rdquo;
                </p>
              )}
              {(order.tips || []).filter((t) => t.status === 'paid').length > 0 && (
                <div className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-rose-500/15 border border-rose-400/30 text-rose-100 text-xs font-bold" data-testid="tip-badge">
                  <Heart size={12} className="fill-rose-300 text-rose-300" />
                  Tipped ${(order.tips || []).filter((t) => t.status === 'paid').reduce((s, t) => s + (t.amount || 0), 0).toFixed(2)}
                </div>
              )}
            </div>
          ) : isBuyer ? (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <p className="text-white font-bold text-sm">How did it go?</p>
                <p className="text-zinc-400 text-xs">Rate this order in 30 seconds — and optionally tip the vendor.</p>
              </div>
              <button
                onClick={() => setRateModalOpen(true)}
                className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-fuchsia-500 to-violet-500 text-white font-bold text-sm inline-flex items-center gap-2 hover:shadow-[0_0_24px_rgba(139,92,246,0.4)] transition-all"
                data-testid="open-rate-modal-btn"
              >
                <Star size={14} className="fill-white" /> Rate this order
              </button>
            </div>
          ) : (
            <p className="text-zinc-400 text-sm">Awaiting buyer rating.</p>
          )}
        </GlassCard>
      )}

      <GlassCard className="p-5" data-testid="order-timeline">
        <p className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold mb-3">Order Timeline</p>
        <div className="space-y-2.5">
          {(order.timeline || []).map((t, i) => (
            <div key={i} className="flex items-start gap-2.5 text-sm">
              <span className="w-2 h-2 rounded-full bg-fuchsia-400 mt-1.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-white capitalize">{t.event.replace(/_/g, ' ')}</p>
                <p className="text-zinc-500 text-[10px]">
                  {new Date(t.at).toLocaleString()} · by {t.by}
                </p>
              </div>
            </div>
          ))}
        </div>
      </GlassCard>

      {/* Message shortcut */}
      <Link
        to={`/messages/${isVendor ? order.buyer_id : order.vendor_user_id}`}
        className="block text-center text-fuchsia-300 hover:text-fuchsia-200 text-sm py-3"
      >
        <MessageSquare size={14} className="inline mr-1" /> Message {other.name || (isVendor ? 'buyer' : 'vendor')}
      </Link>

      {/* Rate + optional tip modal — opens on approve OR via the CTA on a completed/unrated order */}
      <RateAndTipModal
        open={rateModalOpen}
        onClose={() => setRateModalOpen(false)}
        orderId={orderId}
        vendorName={isBuyer ? (other.name || 'the vendor') : 'the vendor'}
        getAuthHeaders={getAuthHeaders}
        onComplete={load}
      />
    </div>
  );
}
